import { Direction } from 'constants/bounding-box';
import { Key, Step } from 'constants/keyboard';
import { CANVAS_ZOOM, LINE_CREATED, SHAPE_CREATED, TEXT_CREATED, WIDGETS_DELETED } from 'constants/mixpanel';
import { useEventListener } from 'hooks/useEventListener';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { fireMPOnDeleteWidget } from 'libs/third-party/Mixpanel/mixpanel.helper';
import throttle from 'lodash.throttle';
import {
  duplicateActiveWidgets,
  groupActiveWidgets,
  moveWidgetByDirection,
  shouldIgnoreEventTarget,
  unGroupActiveWidget,
} from 'modules/Editor/Editor.helpers';
import { getInitialWidgetPosition } from 'modules/Editor/Editor.helpers';
import {
  addNewPage,
  addNewWidget,
  duplicatePage,
  setActivePage,
  setActiveWidget,
  useAppDispatch,
  useAppSelector,
  useAppStore,
} from 'modules/Editor/store';
import {
  selectAllowedKeyboardKeys,
  selectBrandIdx,
  selectHasKeyboardIgnore,
  selectIsSlideView,
  selectIsViewOnly,
  selectZoom,
} from 'modules/Editor/store/editorSettingsSelector';
import { setZoom, toggleSlideView } from 'modules/Editor/store/editorSettingsSlice';
import { useHistory } from 'modules/Editor/store/history/historyManager';
import {
  selectHasReachedPageLimit,
  selectPage,
  selectPageOrder,
  selectWidget,
  selectWidgets,
} from 'modules/Editor/store/infographSelector';
import { removePage, removeWidget, updateWidget } from 'modules/Editor/store/infographSlice';
import { WidgetDirection } from 'modules/Editor/store/infographSlice.types';
import { getFocusedPageNumber } from 'modules/Editor/store/pageControlSlice.helper';
import { PageDirection } from 'modules/Editor/store/pageControlSlice.types';
import { selectActivePage } from 'modules/Editor/store/pageSelector';
import { RootState, setNextActiveWidget, setPreviousActiveWidget } from 'modules/Editor/store/store';
import {
  selectActiveWidgetIds,
  selectActiveWidgetType,
  selectActiveWidgets,
  selectHasActiveWidget,
} from 'modules/Editor/store/widgetSelector';
import { BRAND_INFO_API as BRAND_CACHE_KEY } from 'modules/User/useBrand';
import { ToastContent, useToast } from 'modules/common/components/Toast';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSWRConfig } from 'swr';
import { WidgetType } from 'types/widget.types';
import { isOS } from 'utils/device';
import { BASIC_SHAPE_WIDGET_ITEMS, ICON_TO_WIDGET_SCALE } from 'widgets/BasicShapeWidget/BasicShapeWidget.config';
import { generateDefaultData } from 'widgets/BasicShapeWidget/BasicShapeWidget.helpers';
import {
  generateDefaultData as generateLineWidget,
  getEndPos,
  getMidPosList,
  getStartPos,
} from 'widgets/LineWidget/LineWidget.helpers';
import { generateDefaultData as generateTextWidget } from 'widgets/TextBasedWidgets/TextWidget/TextWidget.helpers';
import { AllWidgetData } from 'widgets/Widget.types';

import { useBoundingBox } from '../BoundingBox/useBoundingBox';
import { createCopyPage } from '../Clipboard';
import { useContextMenu } from '../ContextMenu/ContextMenuProvider';
import { useFocus } from '../Focus';
import { ZOOM_CHANGE_STEP, ZOOM_MAX, ZOOM_MIN } from '../PageControls/ZoomControl.config';
import {
  adjustPageZoom,
  getCurrentScrollRatios,
  scrollToFocusPosition,
  unsetPageSizeProperty,
} from '../PageControls/ZoomControl.helpers';
import { LINE_WIDGET_MENU_ITEMS } from '../WidgetMenu/SideMenu/LineWidgetMenu/LineWidgetMenu.config';
import { allowColorPickerDragElements, hasCustomWidgetKeyOverride } from './KeyboardShortcut.helpers';

// All the global editor keyboard shortcuts are handled here
export const KeyboardShortcut = memo(() => {
  const dispatch = useAppDispatch();
  const store = useAppStore();
  const { cache } = useSWRConfig();

  const { undo, redo } = useHistory();
  const { boundingBox } = useBoundingBox();
  const {
    setWidgetToolbarFocus,
    isCanvasFocused,
    isElementInToolbarFocused,
    setCanvasFocus,
    isWidgetFocused,
    setWidgetFocus,
    isPageSlideFocused,
    pageScrollContainerRef,
    pageSizeAreaRef,
    pageScaleAreaRef,
    pageFocusWrapperRef,
  } = useFocus();

  const toast = useToast();
  const { t } = useTranslation('editor_toolbar');

  const { hideMenu } = useContextMenu();

  const isViewOnly = useAppSelector(selectIsViewOnly);

  const removeActiveWidgets = (): void => {
    const storeState = store.getState() as RootState;
    const activePageId = selectActivePage(storeState);
    const activeWidgetIds = selectActiveWidgetIds(storeState);

    if (activeWidgetIds.length === 1) {
      const widgetType = selectActiveWidgetType(storeState) as WidgetType;
      const widgetData = selectWidget(activeWidgetIds[0])(storeState) as AllWidgetData;
      fireMPOnDeleteWidget(widgetType, widgetData);
    } else if (activeWidgetIds.length > 1) {
      Mixpanel.track(WIDGETS_DELETED.eventName);
    }

    dispatch(setActiveWidget([]));
    dispatch(removeWidget({ pageId: activePageId, widgetIds: activeWidgetIds }));
  };

  const unselectAll = () => {
    if (isWidgetFocused()) setCanvasFocus();
    dispatch(setActiveWidget([]));
  };

  const onSetNextActiveWidget = () => dispatch(setNextActiveWidget());
  const onSetPreviousActiveWidget = () => dispatch(setPreviousActiveWidget());
  const selectAll = () => dispatch(setActiveWidget({ allPageWidgets: true }));
  const onToggleSlideView = () => dispatch(toggleSlideView());

  const removeActivePage = (): void => {
    const storeState = store.getState() as RootState;
    const activePageId = selectActivePage(storeState);
    const pageOrder = selectPageOrder(storeState);

    // if there is only one page, don't remove it
    if (!pageOrder || pageOrder.length === 1) return;

    const isLast = activePageId === pageOrder[pageOrder.length - 1];
    const direction = isLast ? PageDirection.previous : PageDirection.next;

    dispatch(setActivePage({ direction }));
    dispatch(removePage({ pageId: activePageId }));

    const toastId = `remove-page-toast-${Date.now()}`;

    const handleClick = () => {
      if (undo) undo();
      toast.close(toastId);
    };

    toast({
      id: toastId,
      title: t('pageAction.delete'),
      render: ({ title }) => <ToastContent title={title} buttonTitle={t('undoBtn.text')} handleClick={handleClick} />,
    });
  };

  const moveWidget = (key: Key, px: Step = Step.Small) => {
    const move: { deltaX?: number; deltaY?: number } = {};

    if (key === Key.DownArrow) move.deltaY = px;
    if (key === Key.UpArrow) move.deltaY = -px;
    if (key === Key.LeftArrow) move.deltaX = -px;
    if (key === Key.RightArrow) move.deltaX = px;

    return boundingBox.move(move);
  };

  const resizeWidget = (key: Key, px: Step, side = Direction.SOUTH_EAST) => {
    const storeState = store.getState() as RootState;
    const widgets = selectWidgets(storeState);
    const activeWidgetIds = selectActiveWidgetIds(storeState);

    if (!activeWidgetIds || !activeWidgetIds.length) return;

    const widget = widgets[activeWidgetIds[0]];
    const keepRatio = !!(activeWidgetIds.length > 1 || widget.isAspectRatioLocked);
    const resize = { deltaWidth: 0, deltaHeight: 0, keepRatio, side };

    if (key === Key.DownArrow || key === Key.RightArrow) {
      resize.deltaWidth = px;
      resize.deltaHeight = px;
    }

    if (key === Key.UpArrow || key === Key.LeftArrow) {
      resize.deltaWidth = -px;
      resize.deltaHeight = -px;
    }

    return boundingBox.resize(resize);
  };

  const rotateWidget = (key: Key, px: Step = Step.Small) => {
    const rotate = { deltaRotate: 0 };
    if (key === Key.LeftArrow) rotate.deltaRotate = -px;
    if (key === Key.RightArrow) rotate.deltaRotate = px;

    return boundingBox.rotate(rotate);
  };

  const onAddPage = () => {
    const storeState = store.getState() as RootState;
    const activePageId = selectActivePage(storeState);

    const hasReachedPageLimit = selectHasReachedPageLimit(storeState);
    if (hasReachedPageLimit) return;

    dispatch(addNewPage({ insertAfterId: activePageId }));

    const toastId = `remove-page-toast-${Date.now()}`;

    const handleClick = () => {
      if (undo) undo();
      toast.close(toastId);
    };

    toast({
      id: toastId,
      title: t('pageAction.add'),
      render: ({ title }) => <ToastContent title={title} buttonTitle={t('undoBtn.text')} handleClick={handleClick} />,
    });
  };
  const nextPage = () => dispatch(setActivePage({ direction: PageDirection.next }));
  const previousPage = () => dispatch(setActivePage({ direction: PageDirection.previous }));
  const onSwitchPage = (pageNumber: number) => dispatch(setActivePage({ direction: PageDirection.manual, pageNumber }));

  const onDuplicatePage = () => {
    const storeState = store.getState() as RootState;

    const hasReachedPageLimit = selectHasReachedPageLimit(storeState);
    if (hasReachedPageLimit) return;

    const activePageId = selectActivePage(storeState);
    const activePage = selectPage(activePageId)(storeState);
    const widgets = selectWidgets(storeState);
    const pageClipboards = createCopyPage(activePage, widgets);

    dispatch(duplicatePage({ insertAfterId: activePageId, pageClipboards }));

    const toastId = `duplicate-page-toast-${activePageId}`;

    const handleClick = () => {
      if (undo) undo();
      toast.close(toastId);
    };

    toast({
      id: toastId,
      title: t('pageAction.duplicate'),
      render: ({ title }) => <ToastContent title={title} buttonTitle={t('undoBtn.text')} handleClick={handleClick} />,
    });
  };

  const toggleWidgetLock = () => {
    const storeState = store.getState() as RootState;
    const widgets = selectWidgets(storeState);
    const activeWidgetIds = selectActiveWidgetIds(storeState);

    const widgetId = activeWidgetIds[0];
    const widget = widgets[widgetId];

    const updatedWidget = { widgetId, widgetData: { isLocked: !widget.isLocked } };
    dispatch(updateWidget(updatedWidget));
  };

  const onToggleGroup = () => {
    const storeState = store.getState() as RootState;
    const activeWidgets = selectActiveWidgets(storeState);
    const singleGroupSelected =
      activeWidgets.length === 1 && activeWidgets[0].groupMembers.length > 0 && !!activeWidgets[0].groupId;
    const hasGroup = activeWidgets.length > 1;

    if (singleGroupSelected) return unGroupActiveWidget();
    if (hasGroup) return groupActiveWidgets();
  };

  const onThrottledZoomIncrement = throttle(() => {
    const storeState = store.getState() as RootState;
    const zoom = selectZoom(storeState);
    const tempZoom = Math.min(zoom + ZOOM_CHANGE_STEP, ZOOM_MAX);
    const currentScrollRatios = getCurrentScrollRatios(pageScrollContainerRef, pageFocusWrapperRef);

    adjustPageZoom(tempZoom, pageSizeAreaRef?.current, pageScaleAreaRef?.current);
    scrollToFocusPosition(pageScrollContainerRef, pageFocusWrapperRef, currentScrollRatios);
    dispatch(setZoom(tempZoom));
    unsetPageSizeProperty(pageSizeAreaRef);

    Mixpanel.track(CANVAS_ZOOM.eventName, {
      from: CANVAS_ZOOM.from.KeyboardShortCut,
      zoom_level: Math.round(tempZoom * 100),
    });
  }, 100);

  const onThrottledZoomDecrement = throttle(() => {
    const storeState = store.getState() as RootState;
    const zoom = selectZoom(storeState);
    const tempZoom = Math.max(zoom - ZOOM_CHANGE_STEP, ZOOM_MIN);
    const currentScrollRatios = getCurrentScrollRatios(pageScrollContainerRef, pageFocusWrapperRef);

    adjustPageZoom(tempZoom, pageSizeAreaRef?.current, pageScaleAreaRef?.current);
    scrollToFocusPosition(pageScrollContainerRef, pageFocusWrapperRef, currentScrollRatios);
    dispatch(setZoom(tempZoom));
    unsetPageSizeProperty(pageSizeAreaRef);

    Mixpanel.track(CANVAS_ZOOM.eventName, {
      from: CANVAS_ZOOM.from.KeyboardShortCut,
      zoom_level: Math.round(tempZoom * 100),
    });
  }, 100);

  const onAddShapeWidget = (index: number) => {
    const defaultData = BASIC_SHAPE_WIDGET_ITEMS[index];
    let { type, iconDimensions, options } = defaultData;
    const { widthPx: iconWidth, heightPx: iconHeight } = iconDimensions;
    const { top: initialTopPx, left: initialLeftPx } = getInitialWidgetPosition();

    options = { ...options, topPx: initialTopPx, leftPx: initialLeftPx };

    const hasBorder = false;
    const widgetData = generateDefaultData(type, hasBorder, {
      widthPx: iconWidth * ICON_TO_WIDGET_SCALE,
      heightPx: iconHeight * ICON_TO_WIDGET_SCALE,
      ...options,
    });

    dispatch(addNewWidget(widgetData));
    Mixpanel.track(SHAPE_CREATED.eventName, {
      preset: defaultData.label,
      shape_id: type,
      fill: !hasBorder,
      border: hasBorder ? SHAPE_CREATED.border.Solid : SHAPE_CREATED.border.None,
      from: SHAPE_CREATED.from.KeyboardShortcut,
    });
  };

  const onAddLineWidget = () => {
    const basicLineIndex = 0;
    const defaultData = LINE_WIDGET_MENU_ITEMS[basicLineIndex];
    const newData = {
      ...defaultData.options,
      startPos: getStartPos(defaultData.options.posList),
      midPosList: getMidPosList(defaultData.options.posList),
      endPos: getEndPos(defaultData.options.posList),
      isDecorative: defaultData.isDecorative,
    };

    const widgetData = generateLineWidget(defaultData.type, newData);
    const { top: initialTopPx, left: initialLeftPx } = getInitialWidgetPosition();
    widgetData.widgetData = { ...widgetData.widgetData, topPx: initialTopPx, leftPx: initialLeftPx };

    dispatch(addNewWidget(widgetData));
    Mixpanel.track(LINE_CREATED.eventName, {
      from: LINE_CREATED.from.KeyboardShortcut,
      orientation: LINE_CREATED.orientation.Horizontal,
      line_start: LINE_CREATED.lineEdge.None,
      line_end: LINE_CREATED.lineEdge.None,
    });
  };

  const onAddTextWidget = () => {
    const brandIdx = selectBrandIdx(store.getState() as RootState);
    const brandData = cache.get(BRAND_CACHE_KEY)?.data;
    const selectedBrand = brandData?.[brandIdx];
    const fontFamily = selectedBrand?.text?.fontFamily;

    const style = fontFamily ? { fontFamily } : {};
    const { top: topPx, left: leftPx } = getInitialWidgetPosition();
    const type = 'paragraph';
    const textWidget = generateTextWidget(type, undefined, undefined, { topPx, leftPx }, style);

    dispatch(addNewWidget(textWidget));
    Mixpanel.track(TEXT_CREATED.eventName, {
      from: TEXT_CREATED.from.KeyboardShortcut,
      text_style: type.replace(type[0], type[0].toUpperCase()),
    });
  };

  const onKeyDown = (e: KeyboardEvent | Event) => {
    const event = e as KeyboardEvent;
    const target = event.target as HTMLElement;
    const key: Key = event.which || event.keyCode;

    const isControl: boolean = event.ctrlKey;
    const isMod: boolean = event.metaKey || event.ctrlKey;
    const isShift: boolean = event.shiftKey;
    const isAlt: boolean = event.altKey;
    const isWidgetFocus = isWidgetFocused();

    const storeState = store.getState() as RootState;

    const isToolbarFocused = isElementInToolbarFocused();
    const isToolbarExit = isToolbarFocused && key === Key.Escape;
    const hasKeyboardIgnore = selectHasKeyboardIgnore(storeState);
    const allowedKeyboardKeys = selectAllowedKeyboardKeys(storeState);
    const activeWidgetType = selectActiveWidgetType(storeState);

    if (isViewOnly && key !== Key.Enter) return; // if its view only editor, allow only enter key

    if (!isToolbarExit && shouldIgnoreEventTarget(target, allowColorPickerDragElements)) return;
    if (hasKeyboardIgnore && !allowedKeyboardKeys.includes(key)) return;
    if (hasCustomWidgetKeyOverride(key, activeWidgetType)) return;

    const activeWidgets = selectActiveWidgets(storeState);
    const hasActiveWidget = selectHasActiveWidget(storeState);
    const pageOrder = selectPageOrder(storeState);
    const isSlideView = selectIsSlideView(storeState);

    const hasSingleWidgetSelected = activeWidgets?.length === 1;
    const isGroupContainerActive = hasActiveWidget && activeWidgetType === WidgetType.Group;

    switch (key) {
      case Key.Delete:
      case Key.Backspace:
        hideMenu();
        if (isSlideView && !hasActiveWidget) return removeActivePage();
        removeActiveWidgets();
        break;
      case Key.Period:
        e.preventDefault();
        hideMenu();
        if (isControl) return moveWidgetByDirection(WidgetDirection[isShift ? 'Front' : 'Up']);
        break;
      case Key.Comma:
        e.preventDefault();
        hideMenu();
        if (isControl) return moveWidgetByDirection(WidgetDirection[isShift ? 'Back' : 'Down']);
        break;
      case Key.Escape:
        hideMenu();
        if (isToolbarFocused && hasSingleWidgetSelected) {
          return setWidgetFocus(activeWidgets[0]?.id);
        }

        // if a group member focused -> set focus back to the group container
        if (
          isWidgetFocus &&
          hasSingleWidgetSelected &&
          !isGroupContainerActive &&
          activeWidgets[0].groupId &&
          !!activeWidgets[0].groupMembers.length
        ) {
          const nextId = activeWidgets[0].groupId;

          dispatch(setActiveWidget(nextId));
          return setWidgetFocus(nextId);
        }
        unselectAll();
        break;
      case Key.Plus:
        if ((isOS.MAC && isControl) || (isOS.WIN && isControl && isAlt)) {
          onThrottledZoomIncrement();
          hideMenu();
        }
        break;
      case Key.Minus:
        if ((isOS.MAC && isControl) || (isOS.WIN && isControl && isAlt)) {
          onThrottledZoomDecrement();
          hideMenu();
        }
        break;
      case Key.ForwardSlash:
        if (isMod) {
          onToggleSlideView();
          hideMenu();
        }
        break;
      case Key.DownArrow:
      case Key.UpArrow:
      case Key.RightArrow:
      case Key.LeftArrow:
        e.preventDefault();
        hideMenu();
        if ((!hasActiveWidget || isSlideView) && isAlt) {
          if (key === Key.DownArrow) return nextPage();
          if (key === Key.UpArrow) return previousPage();
        }

        const isVertical = key === Key.UpArrow || key === Key.DownArrow;
        const isHorizontal = key === Key.LeftArrow || key === Key.RightArrow;

        if (isMod && isAlt) return resizeWidget(key, Step.Medium);
        if (isMod && isShift && isVertical) return resizeWidget(key, Step.Medium, Direction.SOUTH);
        if (isMod && isShift && isHorizontal) return resizeWidget(key, Step.Medium, Direction.EAST);

        if (isAlt && isHorizontal && isShift) return rotateWidget(key, Step.Large);
        if (isAlt && isHorizontal) return rotateWidget(key, Step.Small);

        if (isMod && isVertical) return resizeWidget(key, Step.Small, Direction.SOUTH);
        if (isMod && isHorizontal) return resizeWidget(key, Step.Small, Direction.EAST);

        if (isShift) return moveWidget(key, Step.Medium);

        return moveWidget(key, 1);
      case Key.Enter:
        if (isMod && (isCanvasFocused() || isSlideView)) {
          hideMenu();
          return onAddPage();
        }
        if (!isMod) {
          if (isCanvasFocused()) {
            hideMenu();
            return onSetNextActiveWidget();
          }

          if (isPageSlideFocused()) {
            const pageNumberToMove = getFocusedPageNumber(pageOrder);

            if (pageNumberToMove) {
              hideMenu();
              return onSwitchPage(pageNumberToMove);
            }
          }
        }

        if (hasSingleWidgetSelected && isWidgetFocus) {
          e.preventDefault();

          // focus the first group member if the group container is active / focused
          if (activeWidgets[0].groupMembers.length && isGroupContainerActive) {
            let nextId = activeWidgets[0].groupMembers[0];

            dispatch(setActiveWidget(nextId));
            return setWidgetFocus(activeWidgets[0]?.id);
          }

          return setWidgetToolbarFocus();
        }
        break;
      case Key.Tab:
        if (hasSingleWidgetSelected) {
          if (isWidgetFocus && isShift) {
            e.preventDefault();
            hideMenu();
            return onSetPreviousActiveWidget();
          }
          if (isWidgetFocus) {
            e.preventDefault();
            hideMenu();
            return onSetNextActiveWidget();
          }
        }
        break;
      case Key.Z:
        if (isMod && !isShift) {
          e.preventDefault();
          hideMenu();
          undo();
        }
        if (event.metaKey && isShift) {
          e.preventDefault();
          hideMenu();
          redo();
        }
        break;
      case Key.Y:
        if (isMod) {
          e.preventDefault();
          hideMenu();
          redo();
        }
        break;
      case Key.A:
        if (isMod) {
          e.preventDefault();
          hideMenu();
          selectAll();
        }
        break;
      case Key.L:
        if (isMod && isAlt && hasSingleWidgetSelected) {
          e.preventDefault();
          hideMenu();
          return toggleWidgetLock();
        }
        if (!isMod && !isAlt && !isShift) {
          return onAddLineWidget();
        }

        break;
      case Key.D:
        if (isMod) {
          event.preventDefault();
          hideMenu();
          if (hasActiveWidget) return duplicateActiveWidgets();
          if (isCanvasFocused() || isSlideView) return onDuplicatePage();
        }
        break;
      case Key.T:
        if (isMod && isAlt && hasActiveWidget && isWidgetFocus) {
          e.preventDefault();
          hideMenu();
          return setWidgetToolbarFocus();
        }
        if (!isMod && !isAlt && !isShift) {
          return onAddTextWidget();
        }
        break;
      case Key.G:
        if (isMod && isAlt) {
          hideMenu();
          return onToggleGroup();
        }
        break;
      case Key.C:
        if (!isMod && !isAlt && !isShift) {
          const circleIndex = 4;
          return onAddShapeWidget(circleIndex);
        }
        break;

      case Key.R:
        if (!isMod && !isAlt && !isShift) {
          const rectangleIndex = 0;
          return onAddShapeWidget(rectangleIndex);
        }
        break;
    }
  };

  useEventListener('keydown', onKeyDown);

  return <></>;
});
KeyboardShortcut.displayName = 'KeyboardShortcut';
